import { useEffect, useState, useMemo } from "react";
import { Download, Search, Mail, Phone, Car, Sparkles, Gauge, Lock, Users } from "lucide-react";
import api from "@/lib/api";

const SOURCE_LABELS = {
  vehicle_alert: { label: "Alerte véhicule", icon: Sparkles, color: "text-[#C8A75A]" },
  similar_search: { label: "Recherche similaire", icon: Search, color: "text-blue-400" },
  confidential_price: { label: "Prix confidentiel", icon: Lock, color: "text-purple-400" },
  sim_qualification: { label: "Qualification simulateur", icon: Gauge, color: "text-emerald-400" },
};

const SOURCE_FILTERS = [
  { key: "all", label: "Tous" },
  { key: "vehicle_alert", label: "Alertes véhicule" },
  { key: "similar_search", label: "Recherches similaires" },
  { key: "confidential_price", label: "Prix confidentiels" },
  { key: "sim_qualification", label: "Qualif. simulateur" },
];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [vehiclesById, setVehiclesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/leads"), api.get("/vehicles", { params: { limit: 500 } })])
      .then(([lr, vr]) => {
        setLeads(lr.data);
        const map = {};
        vr.data.forEach((v) => { map[v.id] = v; });
        setVehiclesById(map);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (source !== "all" && l.source !== source) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          l.email?.toLowerCase().includes(s) ||
          l.name?.toLowerCase().includes(s) ||
          l.phone?.toLowerCase().includes(s) ||
          l.vehicle_recherche?.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [leads, source, q]);

  const exportCsv = () => {
    const headers = ["Date", "Email", "Nom", "Téléphone", "Source", "Véhicule consulté", "Recherche / précision", "Statut"];
    const rows = filtered.map((l) => {
      const v = vehiclesById[l.vehicle_id];
      const vLabel = v ? `${v.brand} ${v.model} ${v.year}` : "";
      return [
        new Date(l.created_at).toLocaleString("fr-BE"),
        l.email || "",
        l.name || "",
        l.phone || "",
        SOURCE_LABELS[l.source]?.label || l.source,
        vLabel,
        (l.vehicle_recherche || "").replace(/\n/g, " "),
        l.status || "new",
      ];
    });
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `kv-international-leads-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight font-light text-white leading-[1.05] mb-3">Leads</h1>
          <p className="text-white/50 text-sm tracking-wide">Prospects capturés · Lecture seule</p>
        </div>
        <button
          data-testid="leads-export-csv"
          onClick={exportCsv}
          disabled={!filtered.length}
          className="bg-[#C8A75A] text-black hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-widest text-xs font-semibold px-6 py-3 rounded-md inline-flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Exporter en CSV ({filtered.length})
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" strokeWidth={1.4} />
          <input
            data-testid="leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher email, nom, téléphone…"
            className="w-full bg-[#1E1E1E] border border-white/10 focus:border-[#C8A75A]/60 outline-none rounded-md pl-9 pr-3 py-3 text-sm text-white placeholder:text-white/30"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SOURCE_FILTERS.map((f) => {
            const active = source === f.key;
            const count = f.key === "all" ? leads.length : leads.filter((l) => l.source === f.key).length;
            return (
              <button
                key={f.key}
                data-testid={`leads-filter-${f.key}`}
                onClick={() => setSource(f.key)}
                className={`text-[10px] uppercase tracking-widest px-3 py-2 rounded-md transition-colors ${
                  active
                    ? "bg-[#C8A75A] text-black font-semibold"
                    : "bg-[#1E1E1E] border border-white/10 text-white/60 hover:text-white hover:border-white/30"
                }`}
              >
                {f.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="bg-[#1E1E1E] border border-white/10 rounded-md h-64 animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-16 text-center">
          <Users className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1} />
          <p className="text-white/50">Aucun lead ne correspond à ces critères.</p>
        </div>
      ) : (
        <div className="bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-white/50 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-6 py-4">Reçu</th>
                <th className="text-left px-6 py-4">Contact</th>
                <th className="text-left px-6 py-4">Source</th>
                <th className="text-left px-6 py-4">Véhicule / Recherche</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const src = SOURCE_LABELS[l.source] || { label: l.source, icon: Sparkles, color: "text-white/60" };
                const Icon = src.icon;
                const v = vehiclesById[l.vehicle_id];
                return (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 py-4 text-white/50 text-xs whitespace-nowrap align-top">
                      {new Date(l.created_at).toLocaleString("fr-BE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-6 py-4 align-top">
                      {l.name && <p className="text-white font-medium">{l.name}</p>}
                      {l.email && (
                        <a href={`mailto:${l.email}`} className="text-[#C8A75A] hover:text-white text-xs inline-flex items-center gap-1.5 mt-0.5">
                          <Mail className="w-3 h-3" />{l.email}
                        </a>
                      )}
                      {l.phone && (
                        <a href={`tel:${l.phone.replace(/\s+/g, "")}`} className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1.5 mt-0.5 block">
                          <Phone className="w-3 h-3" />{l.phone}
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 align-top">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${src.color}`}>
                        <Icon className="w-3 h-3" strokeWidth={1.6} />
                        {src.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-top text-white/70">
                      {v && (
                        <p className="text-white/80 text-xs inline-flex items-center gap-1.5 mb-1">
                          <Car className="w-3 h-3 text-[#C8A75A]" />
                          {v.brand} {v.model} ({v.year})
                        </p>
                      )}
                      {l.vehicle_recherche && <p className="text-white/60 text-xs leading-relaxed italic">« {l.vehicle_recherche} »</p>}
                      {!v && !l.vehicle_recherche && <span className="text-white/30 text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
